import type { ComboboxItemProps, SelectItemProps } from '@ariakit/react';
import {
  Select,
  SelectItem,
  SelectPopover,
  SelectProvider,
  useSelectStore,
  useStoreState,
} from '@ariakit/react';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties, FC, ReactNode, RefObject } from 'react';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getGroupToggleState,
  normalizeGroupOptions,
  toggleAllGroupOptions,
  toggleAllOptions,
} from 'src/components/form/checkbox-group';
import {
  CheckedIcon,
  MixedCheck,
  UncheckedIcon,
} from 'src/components/form/checkbox-icons';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import type {
  GroupedFormOption,
  FormOption,
} from 'src/components/form/form-option';
import { InnerAddon } from 'src/components/form/inner-addon';
import { FieldLabel } from 'src/components/form/label';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { SelectToggleIcon } from 'src/components/form/select/components/select-toggle';
import { Separator } from 'src/components/form/select/components/separator';
import {
  getSelectToggleBaseClasses,
  selectPopoverClasses,
} from 'src/components/form/select/single-select';
import type { SortByOptions } from 'src/components/form/select/utils';
import { selectOptionBaseClasses } from 'src/components/form/select/utils';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Spacer } from 'src/components/layout/spacer';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

type MultiSelectProps<Value extends string> = {
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  options: FormOption<Value>[] | GroupedFormOption<Value>[];
  /** The "(Un)Select All" toggle is enabled by default. */
  toggleAll?: boolean;
  onChange?: (value: Value[]) => void;
  pending?: boolean;
  /**
   * The `placeholder` should guide the user to provide valid input. Refer to the [placeholder pattern documentation](./?path=/docs/patterns-content-placeholders--docs) for more information.
   */
  placeholder?: string;
  sortBy?: SortByOptions;
  initialOpen?: boolean;
} & FormFieldProps;

/**
 * Use this if you have a long list of options (more then ~5) or not enough screen space where the user can select multiple values.
 *
 * If you have a short list of options (e.g. 2-5) or enough screen space, consider using the [Checkbox Group](./?path=/docs/components-form-checkbox-group--docs) component.
 *
 * If you have a long list of options where you want to search, consider using the [Searchable Multi Select](./?path=/docs/components-form-searchable-multi-select--docs) component.
 */
export function MultiSelect<Value extends string>(
  props: MultiSelectProps<Value>
) {
  const { isStacked, minTablet } = useDefaultStacked();

  const {
    name,
    label,
    onChange,
    placeholder,
    pending,
    stacked = isStacked,
    inline,
    options,
    hideLabel = false,
    disabled,
    disabledMessage,
    hint,
    info,
    sortBy = 'label',
    markAsRequired,
    initialOpen,
    toggleAll = true,
  } = props;

  const [field, showError, error, helper] = useCustomField<Value[]>(
    name,
    label
  );

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const id = useId();
  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  const fieldItem = (
    <FieldItem>
      <Tooltip
        content={disabled && disabledMessage ? disabledMessage : undefined}
      >
        {(targetProps) => (
          <SimpleMultiSelect
            id={fieldId}
            options={options}
            showError={showError}
            placeholder={placeholder}
            disabled={disabled}
            pending={pending}
            {...field}
            onChange={(value) => {
              helper.setValue(value);
              onChange?.(value);
            }}
            value={field.value}
            sortBy={sortBy}
            initialOpen={initialOpen}
            toggleAll={toggleAll}
            {...targetProps}
          />
        )}
      </Tooltip>

      {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked} inline={inline}>
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
      />

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
}

type SimpleMultiSelectProps<Value extends string> = {
  id?: string;
  name: string;
  showError?: boolean;
  placeholder?: string;
  options: FormOption<Value>[] | GroupedFormOption<Value>[];
  disabled?: boolean;
  pending?: boolean;
  onChange: (value: Value[]) => void;
  value: Value[];
  sortBy?: SortByOptions;
  initialOpen?: boolean;
  toggleAll?: boolean;
};

function SimpleMultiSelect<Value extends string>({
  id: givenId,
  name,
  showError,
  disabled,
  pending,
  value,
  onChange,
  options: unsortedOptions,
  placeholder = 'Please Select',
  sortBy = 'label',
  initialOpen,
  toggleAll,
}: SimpleMultiSelectProps<Value>) {
  const fallbackId = useId();
  const id = givenId ?? fallbackId;

  const { optionGroups, allChecked, toggleAllChecked } =
    normalizeGroupOptions<Value>(value, unsortedOptions, sortBy);

  const focusRef = useFocusManager<HTMLButtonElement>({ type: 'click' });

  const store = useSelectStore({
    value,
    setValue(value) {
      onChange(value);
    },
    defaultOpen: initialOpen,
  });
  const isOpen = useStoreState(store, 'open');
  const activeId = useStoreState(store, 'activeId');

  const selectedLabels = value
    .map((value) => {
      for (const optionGroup of optionGroups) {
        for (const option of optionGroup.options) {
          if (option.value === value) {
            return option.label;
          }
        }
      }
      throw new Error('Could not find label for value: ' + value);
    })
    .join(', ');

  const showRemoveButton = value.length > 0;

  const noOptions = optionGroups.every(
    (optionGroup) => optionGroup.options.length === 0
  );
  const isDisabled = disabled || noOptions || pending;

  const [popoverFocused, setPopoverFocused] = useState(false);

  return (
    <div data-root>
      <input
        type="hidden"
        data-testid={name}
        value={value.join(',')}
        disabled={isDisabled}
      />
      <SelectProvider store={store}>
        <InnerAddon
          right={
            <div
              className={`
                grid grid-flow-col items-center gap-xs
                legacy:gap-[0.5rem]
              `}
            >
              {showRemoveButton && (
                <div
                  className={`
                    grid grid-flow-col items-center gap-xs
                    legacy:gap-[0.5rem]
                  `}
                >
                  <RemoveButton
                    error={showError}
                    disabled={isDisabled}
                    data-testid={`${name}-remove-selection`}
                    onClick={() => {
                      onChange([]);
                    }}
                    className="[pointer-events:initial]"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </RemoveButton>
                  <Separator error={showError} disabled={isDisabled} />
                </div>
              )}

              <SelectToggleIcon
                showError={showError}
                disabled={isDisabled}
                isOpen={isOpen}
              />
            </div>
          }
        >
          {({ leftBounds, rightBounds }) => (
            <Select
              id={id}
              ref={focusRef}
              disabled={isDisabled}
              data-name={name} // for focus manager
              data-testid={`${name}-togglebutton`}
              data-test-error={showError}
              style={
                {
                  '--field-padding-left': leftBounds
                    ? `${leftBounds.width}px`
                    : undefined,
                  '--field-padding-right': rightBounds
                    ? `${rightBounds.width}px`
                    : undefined,
                } as CSSProperties
              }
              className={cn(
                getSelectToggleBaseClasses({
                  isPlaceholder: !selectedLabels,
                  showError,
                }),
                'text-clip',
                isOpen && popoverFocused && 'not-legacy:focus-style',
                leftBounds &&
                  `
                    pl-(--field-padding-left)
                    legacy:pl-(--field-padding-left)
                  `,
                rightBounds &&
                  `
                    pr-(--field-padding-right)
                    legacy:pr-(--field-padding-right)
                  `
              )}
            >
              <div className="truncate">
                {pending
                  ? 'Loading...'
                  : selectedLabels ||
                    (noOptions ? 'No Options Available' : placeholder)}
              </div>
            </Select>
          )}
        </InnerAddon>

        {createPortal(
          <SelectPopover
            data-testid={`${name}-options`}
            unmountOnHide
            className={selectPopoverClasses}
            onFocus={() => setPopoverFocused(true)}
            onBlur={(event) => {
              // check if focus stays inside the popover on an option
              if (
                event.relatedTarget?.contains(event.target) &&
                event.target.getAttribute('role') === 'option'
              )
                return;

              setPopoverFocused(false);
            }}
          >
            <MultiSelectOptions
              toggleAll={toggleAll}
              toggleAllChecked={toggleAllChecked}
              name={name}
              onChange={onChange}
              optionGroups={optionGroups}
              showError={showError}
              allChecked={allChecked}
              activeId={activeId}
              id={id}
              value={value}
              Item={MultiSelectItem}
            />
          </SelectPopover>,
          document.body
        )}
      </SelectProvider>
    </div>
  );
}

type MultiSelectItemOption = FC<SelectItemProps> | FC<ComboboxItemProps>;

export function MultiSelectOptions<Value extends string>({
  toggleAll,
  toggleAllChecked,
  name,
  onChange,
  optionGroups,
  showError,
  allChecked,
  activeId,
  id,
  value,
  selectionLimit,
  Item,
}: {
  toggleAll?: boolean;
  toggleAllChecked: boolean | 'mixed';
  name: string;
  onChange: (value: Value[]) => void;
  optionGroups: GroupedFormOption<Value>[];
  showError?: boolean;
  allChecked: boolean;
  activeId?: string | null;
  id: string;
  selectionLimit?: number;
  value: Value[];
  /**
   * Specify the item component that should be used for rendering the items. This can be
   * a regular select item or a comboxbox item for our searchable selects.
   */
  Item: MultiSelectItemOption;
}) {
  const maximumItemsSelected =
    selectionLimit !== undefined && value.length >= selectionLimit;
  return (
    <>
      {toggleAll && (
        <Item
          data-testid={`${name}-toggle-all-options`}
          onClick={(event) => {
            event.preventDefault(); // step out of ariakit's event handling
            onChange(
              toggleAllOptions({ toggleAllChecked, optionGroups, value })
            );
          }}
          aria-checked={toggleAllChecked}
        >
          <ItemCheckbox
            checked={toggleAllChecked}
            showError={showError}
            label={
              <strong>{allChecked ? 'Unselect All' : 'Select All'}</strong>
            }
          />
        </Item>
      )}

      {optionGroups.map((optionGroup, index) => (
        <Fragment key={optionGroup.label}>
          {(index !== 0 || toggleAll) && <Spacer />}

          {Boolean(optionGroup.label) && (
            <GroupWithTooltips
              id={id}
              activeId={activeId}
              optionGroup={optionGroup}
              value={value}
              showError={showError}
              name={name}
              onChange={onChange}
              Item={Item}
              hasSelectionLimit={selectionLimit !== undefined}
            />
          )}

          {optionGroup.options.map((option) => (
            <ItemWithTooltips
              key={option.value}
              id={id}
              activeId={activeId}
              option={option}
              optionGroup={optionGroup}
              value={value}
              showError={showError}
              name={name}
              Item={Item}
              maximumItemsSelected={maximumItemsSelected}
            />
          ))}
        </Fragment>
      ))}
    </>
  );
}

function GroupWithTooltips<Value extends string>({
  id,
  activeId,
  optionGroup,
  value,
  showError,
  name,
  onChange,
  hasSelectionLimit,
  Item,
}: {
  id: string;
  activeId: string | null | undefined;
  optionGroup: GroupedFormOption<Value>;
  showError?: boolean;
  value: Value[];
  name: string;
  onChange: (value: Value[]) => void;
  hasSelectionLimit: boolean;
  Item: MultiSelectItemOption;
}) {
  const { groupToggleAllChecked } = getGroupToggleState({
    value,
    optionGroup,
  });

  const infoRef = useRef<HTMLElement>(null);
  const disabledTooltipContent =
    optionGroup.disabled && optionGroup.disabledMessage;
  const groupTestId = `${name}-group-${optionGroup.label}`;
  const groupId = `${id}-${groupTestId}`;
  const active = activeId === groupId;

  return (
    <Tooltip content={disabledTooltipContent} noTabIndex controlOpen={active}>
      {(disabledMessageTargetProps) => (
        <Tooltip
          content={optionGroup.info}
          positionRef={infoRef}
          noTabIndex
          controlOpen={active}
        >
          {(infoTargetProps) => (
            <Item
              {...infoTargetProps}
              {...disabledMessageTargetProps}
              data-testid={groupTestId}
              id={groupId}
              disabled={optionGroup.disabled}
              accessibleWhenDisabled // make it possible to discover disabled items via keyboard
              onClick={(event) => {
                event.preventDefault(); // step out of ariakit's event handling
                onChange(
                  toggleAllGroupOptions({
                    groupToggleAllChecked,
                    optionGroup,
                    value,
                  })
                );
              }}
              aria-checked={groupToggleAllChecked}
            >
              <ItemCheckbox
                checked={groupToggleAllChecked}
                showError={showError}
                disabled={optionGroup.disabled || hasSelectionLimit}
                label={<strong>{optionGroup.label}</strong>}
                info={optionGroup.info}
                infoRef={infoRef}
              />
            </Item>
          )}
        </Tooltip>
      )}
    </Tooltip>
  );
}

function ItemWithTooltips<Value extends string>({
  id,
  activeId,
  option,
  optionGroup,
  value,
  showError,
  name,
  maximumItemsSelected,
  Item,
}: {
  id: string;
  activeId: string | null | undefined;
  option: FormOption<Value>;
  optionGroup: GroupedFormOption;
  showError?: boolean;
  value: Value[];
  name: string;
  maximumItemsSelected?: boolean;
  Item: MultiSelectItemOption;
}) {
  const infoRef = useRef<HTMLElement>(null);
  const disabledTooltipContent =
    (optionGroup.disabled && optionGroup.disabledMessage) ||
    (option.disabled && option.disabledMessage);
  const itemTestId = `${name}:${option.value}`;
  const itemId = `${id}-${itemTestId}`;
  const active = activeId === itemId;
  return (
    <Tooltip content={disabledTooltipContent} noTabIndex controlOpen={active}>
      {(disabledMessageTargetProps) => (
        <Tooltip
          content={option.info}
          positionRef={infoRef}
          noTabIndex
          controlOpen={active}
        >
          {(infoTargetProps) => (
            <Item
              {...infoTargetProps}
              {...disabledMessageTargetProps}
              value={option.value}
              disabled={
                optionGroup.disabled ||
                option.disabled ||
                (maximumItemsSelected && !value.includes(option.value))
              }
              accessibleWhenDisabled // make it possible to discover disabled items via keyboard
              id={itemId}
              data-testid={itemTestId}
            >
              <ItemCheckbox
                checked={value.includes(option.value)}
                showError={showError}
                disabled={
                  optionGroup.disabled ||
                  option.disabled ||
                  (maximumItemsSelected && !value.includes(option.value))
                }
                label={option.label}
                info={option.info}
                infoRef={infoRef}
              />

              {option.hint && !option.hideHint && (
                <FormHint
                  isDisabled={
                    optionGroup.disabled ||
                    option.disabled ||
                    maximumItemsSelected
                  }
                >
                  {option.hint}
                </FormHint>
              )}
            </Item>
          )}
        </Tooltip>
      )}
    </Tooltip>
  );
}

export const multiSelectItemClasses = cn(
  selectOptionBaseClasses,
  `
    [[data-active-item]:not([aria-disabled])]:bg-background-surface-hover
    [[data-active-item]:not([aria-disabled])]:text-foreground-surface-hover
    not-legacy:[&:not([aria-disabled])]:hover:bg-background-surface-hover
    not-legacy:[&:not([aria-disabled])]:hover:text-foreground-surface-hover
  `,
  `
    legacy:p-[0.25rem] legacy:pl-[0.5rem]
    legacy:[[data-active-item]:not([aria-disabled])]:bg-legacy-background
    legacy:[[data-active-item]:not([aria-disabled])]:text-legacy-brand
  `
);

const MultiSelectItem: FC<SelectItemProps> = (props) => {
  return (
    <SelectItem
      {...props}
      className={cn(multiSelectItemClasses, props.className)}
    />
  );
};

const ItemCheckbox: FC<{
  checked: boolean | 'mixed';
  showError?: boolean;
  disabled?: boolean;
  label: string | ReactNode;
  info?: string | ReactNode;
  infoRef?: RefObject<HTMLElement | null>;
}> = ({ checked, disabled, showError, label, info, infoRef }) => {
  return (
    // Same styling as regular checkboxes and radios
    <div
      className={`
        inline-grid grid-flow-col items-baseline gap-xs
        legacy:gap-[0.5rem]
      `}
    >
      <span
        className="pointer-events-none relative top-icon-offset"
        aria-hidden
      >
        {checked === 'mixed' ? (
          <MixedCheck error={showError} disabled={disabled} />
        ) : checked ? (
          <CheckedIcon error={showError} disabled={disabled} />
        ) : (
          <UncheckedIcon error={showError} disabled={disabled} />
        )}
      </span>

      <span>
        {label}
        {!!info && (
          <>
            {' '}
            <FontAwesomeIcon
              forwardedRef={infoRef}
              icon={faInfoCircle}
              className={`
                text-foreground-primary-inverted
                legacy:text-legacy-brand
              `}
            />
          </>
        )}
      </span>
    </div>
  );
};
